import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  MapPin,
  ShieldCheck,
  ExternalLink,
  Loader2,
  AlertCircle,
  Lock,
  ArrowRight,
  CheckCircle2,
  Mail,
  Building2,
  ListChecks,
  Lightbulb,
  Sparkles,
} from "lucide-react";
import Header from "@/components/Header";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

const POPULAR_LOOKUPS = [
  { label: "Mecklenburg County, NC", q: "Mecklenburg" },
  { label: "Maricopa County, AZ", q: "Maricopa" },
  { label: "Miami-Dade County, FL", q: "Miami-Dade" },
  { label: "Dallas County, TX", q: "Dallas" },
  { label: "Cook County, IL", q: "Cook" },
  { label: "Los Angeles County, CA", q: "Los Angeles" },
];

// Heuristic: ZIP-only, "123 Main..." pattern, or "City, ST" pattern => address mode
function looksLikeAddress(s) {
  const q = s.trim();
  if (/^\d{5}$/.test(q)) return true;
  if (/\d+\s+\w+/.test(q)) return true;
  if (q.includes(",") && /\b[A-Z]{2}\b/.test(q)) return true;
  return false;
}

export default function AHJVerify() {
  const { user } = useAuth();
  const isPro = !!user?.subscription?.is_pro;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [geocoded, setGeocoded] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState("name"); // 'name' | 'address'
  const [selected, setSelected] = useState(null);
  const [playbook, setPlaybook] = useState(null);
  const [loadingPlaybook, setLoadingPlaybook] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  // Debounced search — address mode uses /geo/resolve, name mode uses /jurisdictions/search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setGeocoded(null);
      return;
    }
    const addressMode = looksLikeAddress(q);
    setSearchMode(addressMode ? "address" : "name");
    const handle = setTimeout(async () => {
      setSearching(true);
      setError("");
      try {
        if (addressMode) {
          const { data } = await api.get(`/geo/resolve?q=${encodeURIComponent(q)}`);
          if (data.error === "geocoder_unavailable") {
            setError("Address lookup is temporarily unavailable. Try the city or county name instead.");
            setResults([]);
            setGeocoded(null);
          } else {
            setResults(data.results || []);
            setGeocoded(data.geocoded || null);
            if ((data.results || []).length === 0 && q.length >= 5) {
              setError("Couldn't find a matching jurisdiction for that address. Try the city or county name.");
            }
          }
        } else {
          const { data } = await api.get(`/jurisdictions/search?q=${encodeURIComponent(q)}&limit=12`);
          setResults(data.results || []);
          setGeocoded(null);
        }
      } catch (e) {
        setError("Couldn't reach the jurisdiction index. Try again in a moment.");
      } finally {
        setSearching(false);
      }
    }, addressMode ? 500 : 250);
    return () => clearTimeout(handle);
  }, [query]);

  const verify = async (r) => {
    setSelected(r);
    setPlaybook(null);
    setLoadingPlaybook(true);
    setError("");
    try {
      const scope = r.scope === "county" ? "county" : "city";
      const { data } = await api.get(`/ahj/playbook/${r.key}?scope=${scope}`);
      setPlaybook(data);
    } catch (e) {
      if (e.response?.status === 404) {
        // Still show the jurisdiction info even without an intel playbook
        setPlaybook({ key: r.key, scope: r.scope, intel: null, playbook: null, _no_intel: true });
      } else {
        setError("Couldn't load the AHJ playbook. Try another jurisdiction.");
      }
    } finally {
      setLoadingPlaybook(false);
    }
  };

  const reset = () => {
    setSelected(null);
    setPlaybook(null);
    setQuery("");
    setResults([]);
    setGeocoded(null);
    inputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ahj-verify-page">
      <Header />

      {/* HERO + SEARCH */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mb-6" data-testid="ahj-verify-tag">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700">AHJ Verify · Subscriber tool</span>
            </div>
            <h1 className="font-outfit text-5xl md:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Confirm the AHJ for <br className="hidden md:block" />
              <span className="text-emerald-700">your project location.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
              Paste a <span className="font-semibold text-slate-900">street address</span>, ZIP code,
              city, or county. We&apos;ll confirm exactly which Authority Having Jurisdiction is
              responsible for your permits, surface their portal, and pull the playbook our team
              has built from real submittals.
            </p>

            {/* Search bar */}
            <div className="mt-10 relative" data-testid="ahj-verify-search-wrapper">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                <Input
                  ref={inputRef}
                  type="text"
                  data-testid="ahj-verify-input"
                  placeholder="Address, ZIP, city, or county — e.g. '4500 Park Rd, Charlotte NC'"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-14 pl-12 pr-12 text-base border-slate-300 focus-visible:border-emerald-500 focus-visible:ring-emerald-100"
                />
                {searching && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-600 animate-spin" />
                )}
              </div>
              {/* Address mode indicator + geocoded preview */}
              {searchMode === "address" && query.trim().length >= 2 && (
                <div className="mt-2 flex items-center gap-2 text-xs text-slate-600" data-testid="ahj-verify-address-mode">
                  <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-100 text-[10px] font-mono uppercase tracking-[0.14em]">Address mode</Badge>
                  {geocoded?.display_name ? (
                    <span className="text-slate-700 truncate" data-testid="ahj-verify-geocoded">
                      <span className="text-slate-400 mr-1">resolved →</span>
                      {geocoded.display_name}
                    </span>
                  ) : searching ? (
                    <span className="text-slate-500">Geocoding address…</span>
                  ) : (
                    <span className="text-slate-500">Type a full street address or ZIP for best results</span>
                  )}
                </div>
              )}
              {/* Results dropdown */}
              {results.length > 0 && !selected && (
                <div className="absolute z-10 top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-96 overflow-y-auto" data-testid="ahj-verify-results">
                  {searchMode === "address" && (
                    <div className="px-5 py-2 bg-emerald-50 border-b border-emerald-200 text-[11px] font-mono uppercase tracking-[0.16em] text-emerald-800">
                      Verified jurisdictions for this address
                    </div>
                  )}
                  {results.map((r) => (
                    <button
                      key={`${r.scope}-${r.key}`}
                      onClick={() => verify(r)}
                      data-testid={`ahj-result-${r.key}`}
                      className="w-full flex items-center justify-between gap-4 px-5 py-3.5 text-left hover:bg-emerald-50/60 border-b border-slate-100 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`flex-none w-8 h-8 rounded-lg flex items-center justify-center ${r.scope === "county" ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}>
                          {r.scope === "county" ? <Building2 className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{r.label}</div>
                          <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-slate-500">{r.scope} · {r.state_abbr}</div>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-slate-50 border-slate-300 text-slate-700 text-xs flex-none">match {r.score}</Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2" data-testid="ahj-verify-error">
                <AlertCircle className="w-4 h-4 flex-none" />
                {error}
              </div>
            )}

            {!selected && (
              <div className="mt-8" data-testid="popular-lookups">
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">Popular lookups</div>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_LOOKUPS.map((p) => (
                    <button
                      key={p.q}
                      onClick={() => setQuery(p.q)}
                      data-testid={`popular-${p.q.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      className="px-3.5 py-1.5 text-sm bg-slate-100 hover:bg-emerald-100 hover:text-emerald-800 text-slate-700 rounded-full border border-slate-200 hover:border-emerald-300 transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PLAYBOOK RESULT */}
      {selected && (
        <section className="bg-slate-50">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <button onClick={reset} className="text-sm text-emerald-700 hover:text-emerald-900 font-medium inline-flex items-center gap-1.5" data-testid="ahj-verify-reset">
                <ArrowRight className="w-4 h-4 rotate-180" /> New search
              </button>
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100" data-testid="ahj-verify-confirmed">
                <CheckCircle2 className="w-3 h-3 mr-1.5" /> AHJ Confirmed
              </Badge>
            </div>

            {loadingPlaybook && (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 flex items-center justify-center gap-3" data-testid="ahj-verify-loading">
                <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                <span className="text-sm text-slate-600">Pulling the AHJ playbook…</span>
              </div>
            )}

            {playbook && !loadingPlaybook && (
              <PlaybookCard playbook={playbook} selected={selected} isPro={isPro} />
            )}
          </div>
        </section>
      )}

      {/* WHY SUBSCRIBE */}
      {!selected && (
        <section className="bg-slate-900 text-white">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-400 mb-3">Why subscribers verify here first</div>
                <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                  AHJ confusion is the #1 cause of permit delays.
                </h2>
                <p className="mt-5 text-base text-slate-300 leading-relaxed max-w-2xl">
                  Township vs. city? County vs. municipality? Special district overlay? We&apos;ve
                  indexed all 3,221 U.S. counties with real submittal playbooks —
                  so you don&apos;t spend two weeks finding out you filed with the wrong authority.
                </p>
                <ul className="mt-8 space-y-3 max-w-xl">
                  {[
                    "Confirms jurisdiction in seconds — no calling the wrong department.",
                    "Surfaces the right portal URL and intake email (verified from real submittals).",
                    "Includes our submission playbook + tips from prior projects in that AHJ.",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-slate-200">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-none mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-wrap gap-3">
                  <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 h-12 px-7 font-semibold" data-testid="ahj-verify-pricing-cta">
                    <Link to="/pricing">See subscription pricing<ArrowRight className="ml-2 w-4 h-4" /></Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="h-12 px-7 bg-transparent border-slate-700 text-white hover:bg-slate-800" data-testid="ahj-verify-map-cta">
                    <Link to="/map">Open the AHJ Map<ArrowRight className="ml-2 w-4 h-4" /></Link>
                  </Button>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-7">
                  <Sparkles className="w-6 h-6 text-emerald-400 mb-4" />
                  <h3 className="font-outfit text-xl font-bold mb-3">Working a specific project?</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    Tell us the scope and the jurisdiction. We&apos;ll quote your project and
                    handle the permit work end-to-end — intake through Certificate of Occupancy,
                    with weekly status updates direct to your inbox.
                  </p>
                  <div className="mt-5 pt-5 border-t border-slate-700">
                    <Link to="/hire" className="text-sm font-medium text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1.5" data-testid="ahj-verify-hire-link">
                      Hire us as your expediter <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function PlaybookCard({ playbook, selected, isPro }) {
  const noIntel = playbook._no_intel || !playbook.playbook;
  const pb = playbook.playbook || {};
  const intel = playbook.intel || {};
  const overview = pb.overview || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="ahj-verify-playbook">
      {/* LEFT — Overview */}
      <div className="lg:col-span-5">
        <div className="bg-white border border-slate-200 rounded-2xl p-7 sticky top-24">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-700 mb-2">{selected.scope} · {selected.state_abbr}</div>
          <h2 className="font-outfit text-3xl font-bold text-slate-900 leading-tight">{selected.label}</h2>

          {noIntel ? (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="ahj-verify-no-intel">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-700 flex-none mt-0.5" />
                <div>
                  <div className="font-medium text-amber-900">No documented playbook yet</div>
                  <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                    This jurisdiction is in our county index but doesn&apos;t have a verified
                    submittal playbook yet. We can build one for your project — request a quote.
                  </p>
                  <Button asChild size="sm" className="mt-3 bg-amber-700 hover:bg-amber-800 text-white">
                    <Link to="/hire">Request a custom AHJ workup<ArrowRight className="ml-2 w-3.5 h-3.5" /></Link>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-6 space-y-4">
                {overview.jurisdiction && (
                  <Field label="Jurisdiction" value={overview.jurisdiction} />
                )}
                {overview.portal && (
                  <Field label="Permit Portal" value={overview.portal}>
                    {overview.portal_url && (
                      <a href={overview.portal_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-900 font-mono mt-1" data-testid="ahj-verify-portal-link">
                        Open portal <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </Field>
                )}
                {overview.status && (
                  <Field label="Current status signal" value={overview.status} />
                )}
                {overview.last_seen && (
                  <Field label="Last verified" value={new Date(overview.last_seen).toLocaleDateString()} />
                )}
                {pb.primary_contact?.email && (
                  <Field label="Primary contact">
                    <a href={`mailto:${pb.primary_contact.email}`} className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-900 font-mono" data-testid="ahj-verify-contact-email">
                      <Mail className="w-3.5 h-3.5" /> {pb.primary_contact.email}
                    </a>
                  </Field>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap gap-2">
                <Button asChild size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="ahj-verify-hire-cta">
                  <Link to="/hire">Hire us for this AHJ<ArrowRight className="ml-2 w-3.5 h-3.5" /></Link>
                </Button>
                <Button asChild size="sm" variant="outline" className="border-slate-300" data-testid="ahj-verify-detail-cta">
                  <Link to={selected.scope === "county" ? `/county/${selected.key}` : `/place/${selected.key}`}>
                    Open full detail page
                  </Link>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* RIGHT — Playbook detail */}
      <div className="lg:col-span-7 space-y-6">
        {!noIntel && (
          <>
            {/* Submission steps */}
            {pb.submission_steps && pb.submission_steps.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-7" data-testid="ahj-verify-steps">
                <div className="flex items-center gap-2 mb-4">
                  <ListChecks className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-outfit text-xl font-bold text-slate-900">Submission Playbook</h3>
                </div>
                {isPro ? (
                  <ol className="space-y-3">
                    {pb.submission_steps.map((s, i) => (
                      <li key={i} className="flex gap-3" data-testid={`playbook-step-${i}`}>
                        <span className="flex-none w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-mono font-semibold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span className="text-sm text-slate-700 leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <LockedPanel
                    label={`${pb.submission_steps.length} submission steps`}
                    preview={pb.submission_steps[0]}
                  />
                )}
              </div>
            )}

            {/* Tips */}
            {pb.tips && pb.tips.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-7" data-testid="ahj-verify-tips">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                  <h3 className="font-outfit text-xl font-bold text-slate-900">Field Tips</h3>
                </div>
                {isPro ? (
                  <ul className="space-y-3">
                    {pb.tips.map((t, i) => (
                      <li key={i} className="text-sm text-slate-700 leading-relaxed pl-3 border-l-2 border-amber-300" data-testid={`playbook-tip-${i}`}>
                        {t}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <LockedPanel label={`${pb.tips.length} insider tips`} preview="" />
                )}
              </div>
            )}

            {/* Related comms count */}
            {pb.related_communications_count > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-4" data-testid="ahj-verify-comms">
                <div className="flex-none w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-emerald-900 text-sm">
                    {pb.related_communications_count} verified AHJ contacts on file
                  </div>
                  <div className="text-xs text-emerald-800 mt-0.5">
                    Pulled from real submittal correspondence — available to subscribers in your workspace.
                  </div>
                </div>
              </div>
            )}

            {/* Snapshot intel */}
            {intel && (intel.intake_email || intel.escalation_email) && (
              <div className="bg-white border border-slate-200 rounded-2xl p-7" data-testid="ahj-verify-intel-snapshot">
                <h3 className="font-outfit text-xl font-bold text-slate-900 mb-4">Intake & Escalation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {intel.intake_email && (
                    <Field label="Intake email">
                      {isPro ? (
                        <a href={`mailto:${intel.intake_email}`} className="text-sm text-emerald-700 hover:text-emerald-900 font-mono">{intel.intake_email}</a>
                      ) : <LockedText />}
                    </Field>
                  )}
                  {intel.escalation_email && (
                    <Field label="Escalation contact">
                      {isPro ? (
                        <a href={`mailto:${intel.escalation_email}`} className="text-sm text-emerald-700 hover:text-emerald-900 font-mono">{intel.escalation_email}</a>
                      ) : <LockedText />}
                    </Field>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {!isPro && !noIntel && (
          <div className="bg-slate-900 text-white rounded-2xl p-7 flex flex-col md:flex-row items-start md:items-center gap-5" data-testid="ahj-verify-upgrade-banner">
            <Lock className="w-8 h-8 text-emerald-400 flex-none" />
            <div className="flex-1">
              <h3 className="font-outfit text-lg font-bold mb-1">Unlock the full playbook</h3>
              <p className="text-sm text-slate-300">
                Subscribers see every submission step, field tip, and verified AHJ contact for this jurisdiction.
              </p>
            </div>
            <Button asChild className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="ahj-verify-upgrade-btn">
              <Link to="/pricing">Subscribe<ArrowRight className="ml-2 w-3.5 h-3.5" /></Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-slate-500">{label}</div>
      {value && <div className="text-sm text-slate-900 mt-1 font-medium">{value}</div>}
      {children}
    </div>
  );
}

function LockedPanel({ label, preview }) {
  return (
    <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-5 text-center">
      <Lock className="w-5 h-5 text-slate-400 mx-auto mb-2" />
      <div className="text-sm text-slate-700 font-medium">{label}</div>
      {preview && <div className="text-xs text-slate-500 mt-2 italic line-clamp-2">&ldquo;{preview}…&rdquo;</div>}
      <Button asChild size="sm" className="mt-4 bg-slate-900 hover:bg-slate-800 text-white">
        <Link to="/pricing">Subscribe to view<ArrowRight className="ml-1.5 w-3.5 h-3.5" /></Link>
      </Button>
    </div>
  );
}

function LockedText() {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-500 font-mono">
      <Lock className="w-3.5 h-3.5" /> Subscriber only
    </span>
  );
}
